import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { connect } from 'react-redux';
import lodash from 'lodash';
import Loader from '@components/Loader';
import Select from '@components/Select';
import CartCounter from '@app/components/CartCounter';
import { IconMap, ImagesGS } from '@constants/common';
import '@flows/POSPanel/POSPanel.scss';
import './UpdateOrderDialog.scss';

type EditOrderMenuPanelProps = {
    editCart: Record<string, any>;
    onCartChange: (cart: Record<string, any>) => void;
    products: Record<string, any>;
    catOrder: string[];
    restaurantDetails: any;
};

const EditOrderMenuPanel = ({
    editCart,
    onCartChange,
    products,
    catOrder,
    restaurantDetails,
}: EditOrderMenuPanelProps) => {
    const [selectedCat, setSelectedCat] = useState('add-ons');
    const [selectMap, setSelectMap]: any = useState({});

    useEffect(() => {
        setSelectedCat(
            (restaurantDetails?.categoryOrder?.length && restaurantDetails?.categoryOrder[0]) ||
                (restaurantDetails?.menu?.length && Object.keys(restaurantDetails?.menu)[0]) ||
                'add-ons'
        );
    }, [restaurantDetails]);

    const handleSelectChange = (val: any, key: string) => {
        setSelectMap({ ...selectMap, [key]: val });
    };

    const handleSelectBlur = (event: any) => {
        event.stopPropagation();
    };

    const handleAddToCart = (item: any, category: string, name: string) => {
        const selVar =
            selectMap[category + '-' + name] || {
                value: item.variants[0].cost,
                label: `₹${item.variants[0].cost} / ${item.variants[0].size}`,
            };
        const cartKey = category + '-' + name + '-' + selVar.value;
        if (!editCart[cartKey]?.selected) {
            onCartChange({
                ...editCart,
                [cartKey]: {
                    ...item,
                    category,
                    name,
                    selectedVariants: selVar,
                    variants: null,
                    selected: 1,
                },
            });
        }
    };

    const handleCounterUpdate = (cartKey: string, item: any, final: number) => {
        if (final === 0) {
            const next = { ...editCart };
            delete next[cartKey];
            onCartChange(next);
        } else {
            onCartChange({
                ...editCart,
                [cartKey]: { ...item, selected: final },
            });
        }
    };

    const renderItemCounter = (category: string, keyP: string, catP: any) => {
        const cartKey =
            category +
            '-' +
            keyP +
            '-' +
            (selectMap[category + '-' + keyP]
                ? selectMap[category + '-' + keyP]?.value
                : catP.variants[0].cost);
        const matchKey = editCart[cartKey];

        return (
            <Stack direction="row" spacing={1}>
                {!!matchKey?.selected && (
                    <CartCounter
                        initialValue={matchKey.selected}
                        onValueUpdate={(final: number) =>
                            handleCounterUpdate(cartKey, matchKey, final)
                        }
                    />
                )}
                {!matchKey?.selected && (
                    <IconButton disableRipple aria-label="add to cart">
                        <AddShoppingCartIcon />
                    </IconButton>
                )}
            </Stack>
        );
    };

    return (
        <div className="detailsPageWrap admin extra edit-order-menu">
            <ul className="menuNav">
                {(
                    (!!catOrder.length && catOrder) ||
                    (!!products && Object.keys(products).sort()) ||
                    []
                ).map((key: string) => (
                    <li
                        key={key}
                        onClick={() => setSelectedCat(key)}
                        className={key === selectedCat ? 'selected' : ''}
                    >
                        <p className="textMenu">&nbsp;{key.split('-').join(' ')}</p>
                        {IconMap[key] && (
                            <Icon color="primary" className="menuIconDynamic">
                                {IconMap[key]}
                            </Icon>
                        )}
                    </li>
                ))}
            </ul>
            <div className="menuList">
                {!products && <Loader />}
                <div key={selectedCat}>
                    <Box className="product-item">
                        {!!products[selectedCat] &&
                            lodash
                                .sortBy(Object.entries(products[selectedCat]), [
                                    (o: any) => o[1].order,
                                ])
                                .map(([keyP, catP]: any) => (
                                        <div
                                            key={selectedCat + '-' + keyP}
                                            className={`card-wrap food-type food-type_${catP.type}`}
                                        >
                                            <Paper
                                                onClick={() =>
                                                    handleAddToCart(catP, selectedCat, keyP)
                                                }
                                                elevation={3}
                                            >
                                                <span className="product-item__image">
                                                    <img
                                                        src={
                                                            catP.image ||
                                                            ImagesGS.itemPlaceholder
                                                        }
                                                        title={keyP}
                                                        alt={selectedCat}
                                                    />
                                                </span>
                                                <span className="item-wrap">
                                                    <p className="item__name">
                                                        {!!keyP && keyP.replaceAll('-', ' ')}
                                                    </p>
                                                    <div
                                                        onClick={
                                                            catP.variants.length > 1
                                                                ? handleSelectBlur
                                                                : () => {}
                                                        }
                                                    >
                                                        <Select
                                                            className="selectWrap"
                                                            menuPortalTarget={
                                                                typeof document !== 'undefined'
                                                                    ? document.body
                                                                    : null
                                                            }
                                                            menuPosition="fixed"
                                                            menuPlacement="auto"
                                                            styles={{
                                                                menuPortal: (base: Record<string, unknown>) => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                }),
                                                            }}
                                                            onChange={(val: any) =>
                                                                handleSelectChange(
                                                                    val,
                                                                    selectedCat + '-' + keyP
                                                                )
                                                            }
                                                            defaultValue={{
                                                                value: catP.variants[0].cost,
                                                                label: `₹${catP.variants[0].cost} / ${catP.variants[0].size}`,
                                                            }}
                                                            options={catP.variants.map(
                                                                (val: any) => ({
                                                                    value: val.cost,
                                                                    label: `₹${val.cost} / ${val.size}`,
                                                                })
                                                            )}
                                                            isDisabled={
                                                                !(catP.variants.length > 1)
                                                            }
                                                        />
                                                    </div>
                                                    {renderItemCounter(selectedCat, keyP, catP)}
                                                </span>
                                            </Paper>
                                        </div>
                                ))}
                    </Box>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    restaurantDetails: state.common?.selectedRestaurant || {},
    products: state.common?.selectedRestaurant?.menu || {},
    catOrder: state.common?.selectedRestaurant?.categoryOrder || [],
});

export default connect(mapStateToProps)(EditOrderMenuPanel);
